/**
 * i18n 初始化（react-i18next + i18next）
 *
 * 文案数据分两层：
 *   1) 全站共享：i18n-data.json（单一数据源，由 scripts/check-i18n.mjs 校验）
 *   2) 页面私有：由 createPage 通过 meta.messages 注入（如官网的三语文案）
 *
 * 两者按语言合并进 resources，页面私有文案只会被打进对应页面的产物，
 * 不会污染其他落地页的体积。
 *
 * 整个应用只初始化一次；getLang() 决定初始语言，未命中时兜底英语。
 */
import { initReactI18next } from "react-i18next"

import i18n from "i18next"

import messages from "./i18n-data.json"
import { type I18nLang, type LocaleCode, getLang, localeToI18n } from "./lang"
import { setLocale } from "./lang"

/** 语言顺序即 i18next 注册顺序 */
const LANGS: I18nLang[] = ["zh-CN", "en-US", "km-KH"]

type Messages = Partial<Record<I18nLang, Record<string, string>>>

export interface InitI18nOptions {
  /** 页面私有文案，按语言合并到共享文案之上 */
  messages?: Messages
}

const sharedMessages = messages as Messages

/** 共享文案 + 页面私有文案 → i18next resources */
function buildResources(extra?: Messages) {
  return LANGS.reduce(
    (resources, lang) => {
      resources[lang] = {
        translation: {
          ...(sharedMessages[lang] ?? {}),
          ...(extra?.[lang] ?? {}),
        },
      }
      return resources
    },
    {} as Record<I18nLang, { translation: Record<string, string> }>,
  )
}

let initialized = false

export function initI18n(options: InitI18nOptions = {}): typeof i18n {
  if (initialized) return i18n

  i18n.use(initReactI18next).init({
    resources: buildResources(options.messages),
    lng: localeToI18n(getLang()),
    // 系统语言没命中、或某条文案缺失时统一落到英语
    fallbackLng: "en-US",
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  })

  initialized = true
  return i18n
}

/** 切换语言并即时生效 */
export function changeLocale(code: LocaleCode): void {
  setLocale(code)
  void i18n.changeLanguage(localeToI18n(code))
}

export default i18n
